import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { projectService } from "@/services/project.service";
import { aiService } from "@/services/ai.service";
import {
  FileText,
  Layers,
  Plus,
  Trash2,
  Wand2,
  Loader2,
  Save,
  Check,
  GripVertical,
  ChevronRight,
} from "lucide-react";
import type { Project, ProjectPhase } from "@/types/api";

interface Phase {
  name: string;
  description: string;
}

interface ProjectPRDPanelProps {
  project: Project;
  existingPhases?: ProjectPhase[];
  onSetupComplete: () => void;
  onGoToKanban?: () => void;
}

const DEFAULT_PHASES: Phase[] = [
  { name: "Phase 0 - Discovery", description: "Requirements, scope and initial architecture" },
  { name: "Phase 1", description: "" },
  { name: "Phase 2", description: "" },
  { name: "Phase 3", description: "" },
];

export const ProjectPRDPanel = ({ project, existingPhases = [], onSetupComplete, onGoToKanban }: ProjectPRDPanelProps) => {
  const { toast } = useToast();

  const hasExistingPhases = existingPhases.length > 0;

  const [prd, setPrd] = useState(project.prd || "");
  const [phases, setPhases] = useState<Phase[]>(DEFAULT_PHASES);
  const [isSavingPrd, setIsSavingPrd] = useState(false);
  const [prdSaved, setPrdSaved] = useState(!!project.prd);
  const [isCreatingPhases, setIsCreatingPhases] = useState(false);
  const [phasesCreated, setPhasesCreated] = useState(hasExistingPhases);
  const [isGeneratingTasks, setIsGeneratingTasks] = useState(false);

  // ─── Step 1: Save PRD ─────────────────────────────────────────────────────

  const handleSavePrd = async () => {
    if (!prd.trim()) {
      toast({ title: "PRD is empty", description: "Please describe your project requirements", variant: "destructive" });
      return;
    }
    setIsSavingPrd(true);
    try {
      await projectService.updateProject(project.id, { prd });
      setPrdSaved(true);
      toast({ title: "PRD saved" });
    } catch (error: any) {
      toast({
        title: "Error saving PRD",
        description: error?.response?.data?.error || error?.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingPrd(false);
    }
  };

  // ─── Step 2: Create Phases ────────────────────────────────────────────────

  const addPhase = () => {
    setPhases([...phases, { name: "", description: "" }]);
  };

  const removePhase = (index: number) => {
    setPhases(phases.filter((_, i) => i !== index));
  };

  const updatePhase = (index: number, field: keyof Phase, value: string) => {
    setPhases(phases.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const handleCreatePhases = async () => {
    const validPhases = phases.filter(p => p.name.trim());
    if (validPhases.length === 0) {
      toast({ title: "Add at least one phase", variant: "destructive" });
      return;
    }

    setIsCreatingPhases(true);
    try {
      for (let i = 0; i < validPhases.length; i++) {
        await projectService.createPhase(project.id, {
          name: validPhases[i].name,
          description: validPhases[i].description || undefined,
          orderIndex: i,
          status: "Active",
        });
      }
      setPhasesCreated(true);
      toast({ title: `${validPhases.length} phases created` });
    } catch (error: any) {
      toast({
        title: "Error creating phases",
        description: error?.response?.data?.error || error?.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingPhases(false);
    }
  };

  // ─── Step 3: Generate Tasks with AI ───────────────────────────────────────

  const handleGenerateTasks = async () => {
    setIsGeneratingTasks(true);
    try {
      const trackingData = await projectService.getTrackingData(project.id);
      const projectPhases = trackingData.phases || [];

      if (projectPhases.length === 0) {
        toast({ title: "No phases found", description: "Create phases first", variant: "destructive" });
        setIsGeneratingTasks(false);
        return;
      }

      await aiService.generateTasks(project.id, projectPhases);
      toast({ title: "Tasks generated!", description: "Your project is ready" });
      onSetupComplete();
    } catch (error: any) {
      toast({
        title: "Error generating tasks",
        description:
          error?.response?.data?.error ||
          (error instanceof Error ? error.message : "Please try again"),
        variant: "destructive",
      });
    } finally {
      setIsGeneratingTasks(false);
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────────

  const currentStep = phasesCreated ? 3 : prdSaved ? 2 : 1;

  const steps = [
    { num: 1, label: "Define Requirements", done: prdSaved },
    { num: 2, label: "Phases", done: phasesCreated },
    { num: 3, label: "Generate Tasks", done: false },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, i) => (
          <div key={step.num} className="flex items-center flex-1">
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step.done
                  ? 'bg-emerald-500 text-white'
                  : currentStep === step.num
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {step.done ? <Check className="h-4 w-4" /> : step.num}
              </div>
              <span className={`text-sm font-medium hidden sm:block ${
                currentStep === step.num ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {step.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-[2px] mx-3 ${step.done ? 'bg-emerald-500' : 'bg-border'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: PRD */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Product Requirements (PRD)</CardTitle>
          </div>
          <CardDescription>Describe what this project needs to accomplish</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            placeholder="Describe the project goals, features, technical requirements, and any constraints..."
            value={prd}
            onChange={(e) => { setPrd(e.target.value); if (prdSaved) setPrdSaved(false); }}
            rows={6}
            className="resize-none"
          />
          <Button onClick={handleSavePrd} disabled={isSavingPrd || !prd.trim()} className="w-full">
            {isSavingPrd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : prdSaved ? <Check className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {prdSaved ? "PRD Saved — Click to update" : "Save PRD & Continue"}
          </Button>
        </CardContent>
      </Card>

      {/* Step 2: Phases */}
      <Card className={!prdSaved ? 'opacity-40 pointer-events-none' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              <div>
                <CardTitle className="text-lg">Phases</CardTitle>
                <CardDescription className="mt-0.5">Sequential temporal stages of the project</CardDescription>
              </div>
            </div>
            {!phasesCreated && !hasExistingPhases && (
              <Button variant="outline" size="sm" onClick={addPhase}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {hasExistingPhases ? (
            <>
              {existingPhases.map((phase, index) => (
                <div
                  key={phase.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-2 pt-1 text-muted-foreground">
                    <Badge variant="secondary" className="h-6 w-6 p-0 flex items-center justify-center text-xs">
                      {index + 1}
                    </Badge>
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="font-medium text-sm">{phase.name}</div>
                    {phase.description && (
                      <div className="text-xs text-muted-foreground">{phase.description}</div>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between gap-2 text-sm pt-1">
                <div className="flex items-center gap-2 text-emerald-600">
                  <Check className="h-4 w-4" />
                  {existingPhases.length} phases already created
                </div>
                {onGoToKanban && (
                  <Button variant="link" size="sm" onClick={onGoToKanban} className="h-auto p-0 text-muted-foreground">
                    Manage phases in Kanban Board →
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {phases.map((phase, index) => (
                <div
                  key={index}
                  className="group flex items-start gap-3 p-3 rounded-lg border bg-card hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-center gap-2 pt-2 text-muted-foreground">
                    <GripVertical className="h-4 w-4 opacity-30" />
                    <Badge variant="secondary" className="h-6 w-6 p-0 flex items-center justify-center text-xs">
                      {index + 1}
                    </Badge>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Phase name"
                      value={phase.name}
                      onChange={(e) => updatePhase(index, "name", e.target.value)}
                      className="h-8 font-medium"
                      disabled={phasesCreated}
                    />
                    <Input
                      placeholder="Brief description (optional)"
                      value={phase.description}
                      onChange={(e) => updatePhase(index, "description", e.target.value)}
                      className="h-8 text-sm"
                      disabled={phasesCreated}
                    />
                  </div>
                  {!phasesCreated && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      onClick={() => removePhase(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}

              {!phasesCreated ? (
                <Button
                  onClick={handleCreatePhases}
                  disabled={isCreatingPhases || phases.every(p => !p.name.trim())}
                  className="w-full"
                >
                  {isCreatingPhases ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                  Create {phases.filter(p => p.name.trim()).length} Phases & Continue
                </Button>
              ) : (
                <div className="flex items-center gap-2 text-sm text-emerald-600 justify-center py-2">
                  <Check className="h-4 w-4" />
                  Phases created — proceed to generate tasks
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Generate Tasks */}
      <Card className={!phasesCreated ? 'opacity-40 pointer-events-none' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Generate Tasks with AI</CardTitle>
          </div>
          <CardDescription>AI will analyze your PRD and create tasks for each phase</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center py-4 gap-4">
            <p className="text-sm text-muted-foreground text-center max-w-md">
              AI will analyze your PRD and generate actionable tasks for each phase
              following Domotai's project launch playbook.
            </p>
            <Button onClick={handleGenerateTasks} disabled={isGeneratingTasks} size="lg" className="w-full max-w-sm">
              {isGeneratingTasks ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4 mr-2" />
              )}
              {isGeneratingTasks ? "Generating tasks..." : "Generate Tasks with AI"}
            </Button>
            <Button variant="link" size="sm" onClick={onSetupComplete} className="text-muted-foreground">
              or add tasks manually
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
